import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { adminMembersApi } from "@/lib/api/adminMembers";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function EditMemberDialog({ open, onOpenChange, member, onSaved }) {
    const { t, isRTL } = useI18n();
    const [tier, setTier] = useState("SILVER");
    const [expiryDate, setExpiryDate] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [activationStatus, setActivationStatus] = useState("PENDING_PAYMENT");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open && member) {
            setTier(member.tier || "SILVER");
            setExpiryDate(member.expiry_date || "");
            setIsActive(member.is_active !== false);
            setActivationStatus(member.activation_status || "PENDING_PAYMENT");
            setError("");
        }
    }, [open, member]);

    if (!member) return null;

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            const payload = {
                tier,
                expiry_date: expiryDate || null,
                is_active: isActive,
                activation_status: activationStatus,
            };
            const updated = await adminMembersApi.update(member.id, payload);
            toast.success(t("admin.members.updated"));
            onSaved?.(updated);
            onOpenChange(false);
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-md rounded-2xl"
                data-testid="edit-member-dialog"
                dir={isRTL ? "rtl" : "ltr"}
            >
                <DialogHeader>
                    <DialogTitle>{t("admin.members.editTitle")}</DialogTitle>
                    <DialogDescription>{member.full_name} · {member.member_code}</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-2">
                    <div className="rounded-xl border border-border bg-muted/30 px-4 py-3">
                        <p className="text-sm font-semibold">{member.full_name}</p>
                        <p className="text-xs text-muted-foreground font-mono mt-0.5">{member.member_code}</p>
                    </div>
                    <div>
                        <Label>{t("admin.members.fields.tier")}</Label>
                        <Select value={tier} onValueChange={setTier}>
                            <SelectTrigger className="mt-1.5" data-testid="edit-member-tier">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="SILVER">{t("admin.members.tier.SILVER")}</SelectItem>
                                <SelectItem value="GOLD">{t("admin.members.tier.GOLD")}</SelectItem>
                                <SelectItem value="PLATINUM">{t("admin.members.tier.PLATINUM")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="ed_expiry">{t("admin.members.fields.expiryDate")}</Label>
                        <Input
                            id="ed_expiry"
                            type="date"
                            value={expiryDate}
                            onChange={(e) => setExpiryDate(e.target.value)}
                            className="mt-1.5"
                            data-testid="edit-member-expiry"
                        />
                    </div>
                    <div>
                        <Label>{t("admin.members.fields.activationStatus")}</Label>
                        <Select value={activationStatus} onValueChange={setActivationStatus}>
                            <SelectTrigger className="mt-1.5" data-testid="edit-member-activation-status">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="PENDING_PAYMENT">{t("admin.members.statusPendingPayment")}</SelectItem>
                                <SelectItem value="PENDING_REVIEW">{t("admin.members.statusPendingReview")}</SelectItem>
                                <SelectItem value="ACTIVE">{t("admin.members.statusActive")}</SelectItem>
                            </SelectContent>
                        </Select>
                        {member.payment_reference && (
                            <p className="mt-1.5 text-xs text-muted-foreground">
                                {t("admin.members.paymentReferenceLabel")}: <span className="font-mono">{member.payment_reference}</span>
                            </p>
                        )}
                    </div>
                    <div className="flex items-center justify-between rounded-xl border border-border p-3">
                        <div>
                            <Label className="cursor-pointer">{t("admin.members.fields.active")}</Label>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                {isActive ? t("admin.members.activeOn") : t("admin.members.activeOff")}
                            </p>
                        </div>
                        <Switch
                            checked={isActive}
                            onCheckedChange={setIsActive}
                            data-testid="edit-member-active"
                        />
                    </div>

                    {error && (
                        <div
                            className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                            data-testid="edit-member-error"
                        >
                            {error}
                        </div>
                    )}

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-member-cancel">
                            {t("common.cancel")}
                        </Button>
                        <Button type="submit" disabled={submitting} data-testid="edit-member-save">
                            {submitting && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                            {t("common.save")}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
